/**
 * The one way HTML is built in this package.
 *
 * Everything a document prints is tenant data — a customer's name, an item
 * description, a note somebody typed into the invoice. None of it is trusted
 * markup, and this package's whole output ends up in two places that would
 * happily execute it: an iframe in the user's own browser (the Print path) and
 * a Chromium page in the worker (the PDF path). So interpolation ESCAPES, and
 * the only way to compose markup is to hand back a value this module produced.
 *
 * `Html` is a WRAPPER OBJECT rather than a branded string, and that choice is
 * load-bearing. A branded string is a plain string at runtime, so the escaper
 * cannot tell "markup I built" from "a customer's name that happens to contain
 * a tag" — the brand only ever convinced the compiler. A wrapper is
 * distinguishable by `instanceof`, so the escaping decision is made on
 * evidence rather than on a type the caller could have cast.
 *
 * Deliberate composition is `raw()`, which is greppable: there are a handful
 * of call sites in this package and none of them takes a caller's string.
 */
/** Markup this module built. Never constructed from caller input. */
export class Html {
    value;
    constructor(value) {
        this.value = value;
    }
    toString() {
        return this.value;
    }
}
/**
 * The five characters that turn text into markup. `'` and `"` are here because
 * these values also land inside attributes (`title="…"`).
 */
export function escapeHtml(value) {
    return value
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;');
}
/**
 * Mark a string as already-safe markup. Used ONLY for values this package
 * itself produced — never for anything that reached us from a caller, a
 * request, or a database row.
 */
export function raw(markup) {
    return new Html(markup);
}
/** The empty fragment, so a conditional branch has something to be. */
export const EMPTY = raw('');
/**
 * `html` — the tagged template. Interpolations are escaped unless they are
 * already `Html`; arrays are joined so a `.map()` of rows drops straight in;
 * `null`, `undefined` and `false` render as nothing, which is what an absent
 * optional field should look like.
 */
export function html(strings, ...values) {
    let out = strings[0] ?? '';
    for (let i = 0; i < values.length; i += 1) {
        out += render(values[i]);
        out += strings[i + 1] ?? '';
    }
    return new Html(out);
}
function render(value) {
    if (value === null || value === undefined || value === false)
        return '';
    if (value instanceof Html)
        return value.value;
    if (Array.isArray(value))
        return value.map(render).join('');
    return escapeHtml(String(value));
}
/**
 * Escape a value for an attribute whose content is not markup at all — a
 * `lang`, a `dir`, a class name. Identical rules; a separate name because a
 * reader should be able to see which context a value landed in.
 */
export const attr = escapeHtml;
//# sourceMappingURL=html.js.map